import { useCallback } from 'react';

import type { Message as StoredMessage } from '../../api/generated-api';
import {
  useConversationServiceShareRecipientsMutation,
  useLazyConversationServiceListConversationMessagesQuery,
} from '../../api/overrides';
import { parseWireArtifacts } from '../../sse/wireArtifacts';
import type { ArtifactRef } from '../../state/chatTypes';
import type { ShareRecipient } from '../ShareViaEmailDialog';
import {
  findShareArtifactId,
  SHARE_TYPE,
  type ShareType,
} from './share-target';
import {
  findStoredResponse,
  hasReadClosestStoredResponse,
  ShareNotSentError,
  toShareRecipients,
} from './utils';

/** Stored messages read per request; the API's maximum page. */
export const STORED_MESSAGES_PAGE_SIZE = 100;

/**
 * Pages of stored messages read before giving up, so a cursor that
 * never runs out cannot hold a share open indefinitely.
 */
export const MAX_STORED_MESSAGE_PAGES = 10;

/** Identifies the assistant response being shared. */
export type UseShareResponseParams = {
  conversationId: string;
  /** Id the response arrived under, from `messageDone` or the sync reply. */
  messageId: string;
  /** The response's raw text, used to find its stored copy. */
  content: string;
  /** When the response arrived, used to tell identical answers apart. */
  createdAt: string;
  /** What the share sends. Defaults to the response alone. */
  shareType?: ShareType;
  /** The tables and charts the response showed, when it carried them. */
  artifacts?: ReadonlyArray<ArtifactRef>;
};

/** The stored message a share names, and the table or chart it carries. */
type ShareTarget = {
  messageId: string;
  artifactId?: string;
};

/**
 * Whether `messageId` is already the backend's stored id rather than
 * the turn id stamped on stream events. Stored ids are prefixed with
 * the conversation they belong to.
 */
const isStoredMessageId = (conversationId: string, messageId: string) =>
  messageId.startsWith(`${conversationId}:`);

/**
 * Sends one assistant response by email through the Sidekick share
 * endpoint, on its own or as the table or chart it showed. The backend
 * reads the message and the artifact itself and composes the email, so
 * the request carries only ids and recipients.
 *
 * `/share` resolves the message by its stored id, but a streamed or
 * sync answer arrives under a per-turn id that is never stored. When
 * that is what the bubble holds, the stored copy is looked up by
 * content in the conversation's stored messages, newest first. Matching
 * by content rather than position keeps a turn that failed to store
 * from shifting later answers onto the wrong email; when answers
 * repeat, the one stored closest to `createdAt` is taken, so paging
 * continues until no older message could be closer.
 *
 * A table or chart share also names the artifact, which must belong to
 * the stored message it shares. The response's own `artifacts` are used
 * when it already holds its stored id; otherwise the artifact is read
 * off the stored copy the lookup finds.
 *
 * @param params - The conversation, the id the response arrived under, its text, when it arrived, what to share, and the artifacts it showed.
 * @returns A callback that resolves once the share is accepted. It
 * rejects with a {@link ShareNotSentError} when the stored copy or the
 * shared table or chart cannot be found within the page limit, and
 * with the request error when `/share` refuses.
 */
export const useShareResponse = ({
  conversationId,
  messageId,
  content,
  createdAt,
  shareType = SHARE_TYPE.RESPONSE,
  artifacts,
}: UseShareResponseParams): ((
  recipients: ShareRecipient[]
) => Promise<void>) => {
  const [share] = useConversationServiceShareRecipientsMutation();
  const [listMessages] =
    useLazyConversationServiceListConversationMessagesQuery();

  const findStoredMessage = useCallback(async (): Promise<
    StoredMessage | undefined
  > => {
    const storedId = isStoredMessageId(conversationId, messageId)
      ? messageId
      : undefined;
    const pick = (read: StoredMessage[]) =>
      storedId === undefined
        ? findStoredResponse(read, content, createdAt)
        : read.find(({ id }) => id === storedId);
    const read: StoredMessage[] = [];
    let pageToken: string | undefined;
    for (let page = 0; page < MAX_STORED_MESSAGE_PAGES; page += 1) {
      const { messages = [], pagination } = await listMessages({
        conversationId,
        pageSize: STORED_MESSAGES_PAGE_SIZE,
        pageToken,
      }).unwrap();
      read.push(...messages);
      pageToken = pagination?.nextPageToken;
      const isSettled =
        storedId === undefined
          ? hasReadClosestStoredResponse(read, content, createdAt)
          : pick(read) !== undefined;
      if (!pageToken || isSettled) {
        return pick(read);
      }
    }
    return undefined;
  }, [content, conversationId, createdAt, listMessages, messageId]);

  const resolveShareTarget = useCallback(async (): Promise<ShareTarget> => {
    const knownArtifactId = findShareArtifactId(artifacts, shareType);
    if (
      isStoredMessageId(conversationId, messageId) &&
      (shareType === SHARE_TYPE.RESPONSE || knownArtifactId !== undefined)
    ) {
      return { messageId, artifactId: knownArtifactId };
    }
    const stored = await findStoredMessage().catch((error: unknown) => {
      throw new ShareNotSentError('Stored messages could not be read', error);
    });
    if (stored?.id === undefined) {
      throw new ShareNotSentError(
        'No stored copy of the shared response was found'
      );
    }
    if (shareType === SHARE_TYPE.RESPONSE) {
      return { messageId: stored.id };
    }
    const artifactId = findShareArtifactId(
      parseWireArtifacts(stored.artifacts),
      shareType
    );
    if (artifactId === undefined) {
      throw new ShareNotSentError(
        'The stored response holds no table or chart to share'
      );
    }
    return { messageId: stored.id, artifactId };
  }, [artifacts, conversationId, findStoredMessage, messageId, shareType]);

  return useCallback(
    async (recipients: ShareRecipient[]): Promise<void> => {
      const target = await resolveShareTarget();
      await share({
        shareRequest: {
          conversationId,
          messageId: target.messageId,
          shareType,
          recipients: toShareRecipients(recipients),
          ...(target.artifactId === undefined
            ? {}
            : { artifactIds: [target.artifactId] }),
        },
      }).unwrap();
    },
    [conversationId, resolveShareTarget, share, shareType]
  );
};
